import { config } from "../config.js";

// SENSEOFVOID の model-preview/graphics-quality.js をそのまま持ってきた(2026-10-29)。描画の細かさの自動調整と、手動の描画レベル。
// SENSEOFDOPA では、どの段にいるかから演出の量(config.graphics.levels の particleScale・bloom・stars)も決める(view/index.js の effectLevel)。

// 描画の品質の自動調整(2026-10-03 オーナー決定。仕様書「グラフィックの自動最適化」の章の第一歩)。
// 変えるのは「描く画素の細かさ」(renderer.setPixelRatio)だけ。試合の状態・機体・ビーム・ターゲットの印などの見え方の有無は変えない。
//   - 基本の細かさ: 画面の倍率(devicePixelRatio)に上限(config.graphics.maxPixelRatio=2)を付ける。
//     iPhoneなど倍率3の画面では、見た目はほぼ同じまま、描く画素が半分以下になる。
//   - AUTO: 実際のFPSが下がった状態がしばらく続いたら、細かさを1段ずつ下げる(autoScales)。余裕が戻った状態が
//     もっと長く続いたら、1段ずつ戻す(上げ下げのたびに見た目がちらつかないよう、ゆっくり・間を空けて)。
//   - mode "full": 上限もAUTOも使わない(以前と同じ。?quality=full で比べる確認用)。
//   - 手動の描画レベル(2026-10-06。設定の「描画レベル」。setLevel): 自動調整をやめ、config.graphics.levelsの段階の細かさに固定する。
//     AUTOのFPSの目安は、描画の回数の上限(設定)が60より低いとき、その割合で下げて判断する(上限のせいで下げないように)。
//   - 軽い表示(2026-10-20。config.graphics.lite。isLite): 手動のLOWのとき、またはAUTOで細かさを下限(autoScalesの最後)まで
//     下げてもまだ遅いとき(AUTOの最後の1段。細かさは下限のまま)。大軍戦は画素を減らしても軽くならないため、描く物の量を減らす。
export function createGraphicsQuality({ renderer, mode = "auto", onChange = () => {} }) {
  let fixedLevel = null; // 手動の描画レベル("low" | "mid" | "high")。nullならmodeのまま
  const cfg = () => config.graphics;
  let level = 0; // AUTOの何段目か(0=いちばん細かい。autoScalesの数と同じ段=細かさは下限のまま軽い表示)
  let windowTime = 0; // FPSを測っている区間の長さ(秒)
  let windowFrames = 0;
  let lowSeconds = 0; // FPSが低い状態が続いている長さ
  let highSeconds = 0; // FPSに余裕がある状態が続いている長さ
  let sinceChange = 0; // 最後に段を変えてからの時間
  let lastFps = 0;

  function basePixelRatio() {
    const dpr = window.devicePixelRatio || 1;
    return mode === "full" ? dpr : Math.min(dpr, cfg().maxPixelRatio);
  }

  function scale() {
    if (mode === "full") return 1;
    if (fixedLevel) return cfg().levels[fixedLevel].pixelScale;
    const scales = cfg().autoScales;
    return scales[Math.min(level, scales.length - 1)];
  }

  // 軽い表示か(config.graphics.lite)
  function isLite() {
    if (mode === "full") return false;
    if (fixedLevel) return fixedLevel === "low";
    return level >= cfg().autoScales.length;
  }

  function apply() {
    renderer.setPixelRatio(basePixelRatio() * scale());
    onChange(info());
  }

  // 描画のたびに呼ぶ。frameDt: 前回の描画からの実時間(秒)。measuring: 測ってよい場面か
  // (試合中で、描く回数を下げていないとき。止めている間・観戦中の毎秒30回の間は測らない)
  // targetFps: 描画の回数の上限(設定。上限なしならnull)
  function sample(frameDt, measuring, targetFps = null) {
    if (mode !== "auto" || fixedLevel) return;
    sinceChange += frameDt;
    if (!measuring || frameDt <= 0) {
      windowTime = 0;
      windowFrames = 0;
      return;
    }
    windowTime += frameDt;
    windowFrames++;
    if (windowTime < 1) return; // 1秒ごとに判断する
    const fps = windowFrames / windowTime;
    lastFps = fps;
    const c = cfg();
    const ratio = targetFps ? Math.min(1, targetFps / 60) : 1;
    if (fps < c.autoDownFps * ratio) {
      lowSeconds += windowTime;
      highSeconds = 0;
    } else if (fps >= c.autoUpFps * ratio) {
      highSeconds += windowTime;
      lowSeconds = 0;
    } else {
      lowSeconds = 0;
      highSeconds = 0;
    }
    windowTime = 0;
    windowFrames = 0;
    if (sinceChange < c.autoMinIntervalSeconds) return;
    if (lowSeconds >= c.autoDownAfterSeconds && level < c.autoScales.length) {
      level++;
      lowSeconds = 0;
      sinceChange = 0;
      apply();
    } else if (highSeconds >= c.autoUpAfterSeconds && level > 0) {
      level--;
      highSeconds = 0;
      sinceChange = 0;
      apply();
    }
  }

  function info() {
    return { mode: fixedLevel || mode, level, scale: scale(), lite: isLite(), pixelRatio: renderer.getPixelRatio(), lastFps };
  }

  // 描画レベル: "auto" | "low" | "mid" | "high"
  function setLevel(name) {
    fixedLevel = name === "auto" ? null : name;
    if (!fixedLevel) {
      level = 0; // AUTOに戻したら、いちばん細かい段から測り直す
      lowSeconds = highSeconds = 0;
    }
    apply();
  }

  apply();
  window.addEventListener("resize", apply); // 画面の倍率が変わる(別の画面へ移す・拡大縮小)ことがある

  return { sample, info, apply, setLevel, isLite };
}
